import axios from "axios";
import { unauthorizedInterceptors } from "../../../core/utilities/functions";

const overpaymentAPI = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/accounting`,
  headers: {
    "Content-Type": "application/json",
  },
});
unauthorizedInterceptors(overpaymentAPI);

const setAuthHeader = () => {
  const token = localStorage.getItem("accessToken");
  if (token) {
    overpaymentAPI.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }
};

// Simple in‑memory GET cache for overpayment endpoints
const overpaymentGetCache = new Map(); // key -> { data, time }

export const clearOverpaymentCache = () => {
  overpaymentGetCache.clear();
};

// Product API Functions
export const getAllOverpayments = async (filters = {}) => {
  setAuthHeader();
  const params = new URLSearchParams();
  if (filters.name) params.append("name", filters.name);
  if (filters.brand) params.append("brand", filters.brand);
  if (filters.partner) params.append("partner", filters.partner);
  if (filters.transaction) params.append("transaction", filters.transaction);
  if (filters.has_remaining)
    params.append("has_remaining", filters.has_remaining);

  try {
    const response = await overpaymentAPI.get("/partner-overpayments/", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching overpayments:", error);
    throw error;
  }
};

// Create an overpayment from a transaction
export const createOverpaymentFromTransaction = async (data) => {
  setAuthHeader();
  try {
    const response = await overpaymentAPI.post(
      "/partner-overpayments/create_from_transaction/",
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error creating overpayment:", error);
    throw error;
  }
};

// Get overpayments for a specific transaction
export const getTransactionOverpayments = async (transactionId) => {
  setAuthHeader();
  try {
    const response = await overpaymentAPI.get(
      `/partner-overpayments/get_transaction_overpayments/?transaction_id=${transactionId}`
    );
    // Backend returns { transaction: {...}, overpayments: [...] }
    // Return just the overpayments array
    return response.data.overpayments || [];
  } catch (error) {
    console.error("Error fetching transaction overpayments:", error);
    // Return empty array if transaction has no overpayments (404 is OK)
    if (error.response?.status === 404) {
      return [];
    }
    throw error;
  }
};

export const getPartnerTransactions = async (
  partnerId,
  brandId = null,
  transactionType = null,
  sortBy = null,
  startDate = null,
  endDate = null,
  partnerType = null,
  unapproved = false,
  showCancelled = false
) => {
  setAuthHeader();
  const params = new URLSearchParams();
  params.append("partner_id", partnerId);
  if (brandId) params.append("brand_id", brandId);
  if (transactionType && transactionType !== "all")
    params.append("transaction_type", transactionType);
  if (sortBy) params.append("sort_by", sortBy);
  if (startDate && endDate) {
    params.append("start_date", startDate);
    params.append("end_date", endDate);
  }
  if (partnerType) {
    params.append("partner_type", partnerType);
  }
  if (unapproved) {
    params.append("unapproved", "true");
  }
  if (showCancelled) {
    params.append("show_cancelled", "true");
  }

  try {
    const response = await overpaymentAPI.get("/partner-transactions/", {
      params,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching partner transactions:", error);
    throw error;
  }
};

export const getPartnerTransactionsPDF = async (
  partnerId,
  brandId = null,
  transactionType = null,
  sortBy = null,
  startDate = null,
  endDate = null,
  partnerType = null,
  unapproved = false
) => {
  setAuthHeader();
  const params = new URLSearchParams();
  params.append("partner_id", partnerId);
  if (brandId) params.append("brand_id", brandId);
  if (transactionType && transactionType !== "all")
    params.append("transaction_type", transactionType);
  if (sortBy) params.append("sort_by", sortBy);
  if (startDate && endDate) {
    params.append("start_date", startDate);
    params.append("end_date", endDate);
  }
  if (partnerType) params.append("partner_type", partnerType);
  if (unapproved) {
    params.append("unapproved", "true");
  }

  const response = await overpaymentAPI.get(
    "/partner-transactions/generate_pdf/",
    {
      params,
      responseType: "blob",
    }
  );

  return response.data;
};

export const getPartnerStatementPDF = async (
  partnerId,
  brandId = null,
  partnerType = null,
  startDate = null,
  endDate = null,
  showCancelled = false,
  unapproved = false
) => {
  setAuthHeader();
  const params = {};
  params.partner_id = partnerId;
  if (brandId) params.brand_id = brandId;
  if (partnerType) params.partner_type = partnerType;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  params.show_cancelled = showCancelled ? "true" : "false";
  params.unapproved = unapproved ? "true" : "false";

  const response = await overpaymentAPI.get(
    "/partner-transactions/generate_statement_pdf/",
    {
      params,
      responseType: "blob",
    }
  );

  return response.data;
};
export const getPartnerTransactionsExcel = async (
  partnerId,
  brandId = null,
  transactionType = null,
  sortBy = null,
  startDate = null,
  endDate = null,
  partnerType = null,
  unapproved = false
) => {
  setAuthHeader();
  const params = new URLSearchParams();
  params.append("partner_id", partnerId);
  if (brandId) params.append("brand_id", brandId);
  if (transactionType && transactionType !== "all")
    params.append("transaction_type", transactionType);
  if (sortBy) params.append("sort_by", sortBy);
  if (startDate && endDate) {
    params.append("start_date", startDate);
    params.append("end_date", endDate);
  }
  if (partnerType) params.append("partner_type", partnerType);
  if (unapproved) {
    params.append("unapproved", "true");
  }

  const response = await overpaymentAPI.get(
    "/partner-transactions/generate_excel/",
    {
      params,
      responseType: "blob",
    }
  );

  return response.data;
};

// Get overpayment details including allocations
export const getOverpaymentDetails = async (overpaymentId) => {
  setAuthHeader();
  try {
    const response = await overpaymentAPI.get(
      `/overpayment-allocations/get_overpayment_details/?overpayment_id=${overpaymentId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching overpayment details:", error);
    throw error;
  }
};

// Delete an overpayment by ID
export const deleteOverpayment = async (overpaymentId) => {
  setAuthHeader();
  try {
    const response = await overpaymentAPI.delete(
      `/partner-overpayments/${overpaymentId}/`
    );
    return response.data;
  } catch (error) {
    console.error("Error deleting overpayment:", error);
    throw error;
  }
};
